import { useQueryClient } from '@tanstack/react-query';
import { useState, useId } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { mfaFresh, useAuth } from '../lib/auth';
import { label } from '../lib/format';
import { ACTION_TYPE, KIND, RISK, ROLE, say, when } from '../lib/labels';
import { itemChanges, policyView } from '../lib/policy-view';
import { ActionButton, ErrorBox, Load, Page, Section, Status, Table, useApi } from '../ui/kit';
import { changeCount, CompareView, day, ReadView, Signatures, VersionStrip } from '../ui/versions';
import { ago } from '../ui/phone';

type Inbox = {
  mfa_fresh: boolean;
  policies: Array<{ id: string; type: string; subject: string; version: number; body_sha256: string; created_at: string; roles_needed: string[]; can_sign: boolean; signed_by: string[] }>;
  actions: Array<{ id: string; action_type: string; risk_class: string; reason: string; created_at: string }>;
  first_batches?: Array<{ campaign_id: string; name: string; waiting: number; ready: boolean; can_review: boolean }>;
};

export function Approvals() {
  const { mfaAgeMs } = useAuth();
  return (
    <Page title="Approvals">
      {!mfaFresh(mfaAgeMs) ? <div className="banner" role="status">Approving needs a second factor in the last 12 hours. <Link to="/mfa">Verify now</Link></div> : null}
      <Load<Inbox> path="/portal/approvals">
        {(a) => (
          <>
            <Section title="Policies to sign">
              <Table rows={a.policies} rowLink={(p) => `/approvals/${p.id}`} columns={[
                { key: 'type', label: 'What', render: (p) => KIND[p.type] ?? label(p.type) }, 'version',
                { key: 'roles_needed', label: 'Needs', render: (p) => p.roles_needed.map((r) => say(ROLE, r)).join(' + ') },
                { key: 'signed_by', label: 'Signed', render: (p) => (p.signed_by.length ? p.signed_by.map((r) => say(ROLE, r)).join(', ') : '—') },
                { key: 'can_sign', label: 'You can sign', render: (p) => (p.can_sign ? 'Yes' : 'No') }, { key: 'created_at', label: 'Sent', render: (p) => when(p.created_at) },
              ]} empty="Nothing waiting for a signature."

                phone={{ title: (p) => `${KIND[p.type] ?? say(KIND, p.type)}, version ${p.version}`, status: (p) => <span className={`chip chip-${p.can_sign ? 'good' : 'neutral'}`}>{p.can_sign ? 'You can sign' : 'Waiting'}</span>,
                  meta: (p) => [`Needs ${p.roles_needed.map((r) => say(ROLE, r)).join(' + ')}`, p.signed_by.length ? `signed by ${p.signed_by.map((r) => say(ROLE, r)).join(', ')}` : null, ago(p.created_at)] }} />
            </Section>
            {a.first_batches?.length ? (
              <Section title="First emails to review" need="Nothing of these campaigns is sent until you review them">
                <Table rows={a.first_batches} rowLink={(b) => `/campaigns/${b.campaign_id}`} columns={[
                  { key: 'name', label: 'Campaign' }, { key: 'waiting', label: 'Emails waiting' },
                  { key: 'can_review', label: 'You can review', render: (b) => (b.can_review ? 'Yes' : 'Owner, admin or sales approver') },
                ]} phone={{ title: (b) => b.name, status: (b) => <span className="chip chip-good">{b.waiting} to review</span>, meta: () => ['Approve, edit or remove each on the campaign page'] }} />
              </Section>
            ) : null}
            <PoliciesInUse />
            <Section title="Revenue Actions waiting for a person or a policy">
              <Table rows={a.actions} columns={[{ key: 'action_type', label: 'Action', render: (r) => say(ACTION_TYPE, r.action_type) }, { key: 'risk_class', label: 'Reach', render: (r) => say(RISK, r.risk_class) }, 'reason', { key: 'created_at', label: 'Proposed', render: (r) => when(r.created_at) }, { key: 'id', label: '', render: (r) => (
                r.risk_class === 'internal' || r.risk_class === 'own_systems'
                  ? <span className="actions"><ActionButton path={`/portal/revenue-actions/${r.id}/approve`} label="Approve" variant="primary" disabled={!mfaFresh(mfaAgeMs)} title={mfaFresh(mfaAgeMs) ? undefined : 'Verify your second factor first'} />
                      <ActionButton path={`/portal/revenue-actions/${r.id}/reject`} label="Reject" variant="danger" confirm="Reject this action?" /></span>
                  : <span className="small muted">Needs an approved {r.risk_class === 'spend' ? 'audience' : 'outbound'} policy</span>
              ) }]} empty="No actions waiting."
                phone={{ title: (r) => label(r.action_type), status: (r) => <Status value={r.risk_class} />, meta: (r) => [r.reason, ago(r.created_at)] }} />
            </Section>
          </>
        )}
      </Load>
    </Page>
  );
}

const SUBJECT = (s: string) => (s === '*' ? 'Whole workspace' : /^[0-9a-f-]{36}$/.test(s) ? 'One campaign' : label(s));

/** Every policy, any status, newest version first within each kind: each opens its page with its versions. */
function PoliciesInUse() {
  const all = useApi<{ data: Policy[] }>('/v1/policies');
  return (
    <Section title="Policies in use">
      {all.isLoading ? <p className="muted small" role="status">Loading…</p> : all.error ? <ErrorBox error={all.error} /> : (
        <Table rows={all.data?.data ?? []} rowLink={(p) => `/approvals/${p.id}`} caption="Every policy and its versions" columns={[
          { key: 'type', label: 'What', render: (p) => KIND[p.type] ?? label(p.type) },
          { key: 'subject', label: 'Covers', render: (p) => SUBJECT(p.subject) },
          { key: 'version', label: 'Version', render: (p) => `v${p.version}` },
          { key: 'status', label: 'State', render: (p) => <span className={`chip ${p.status === 'approved' ? 'done' : ''}`}>{STATUS[p.status] ?? p.status}</span> },
          { key: 'created_at', label: 'Drafted', render: (p) => day(p.created_at) },
        ]} empty="No policies yet."
          phone={{ title: (p) => `${KIND[p.type] ?? label(p.type)}, version ${p.version}`, status: (p) => <span className={`chip ${p.status === 'approved' ? 'done' : ''}`}>{STATUS[p.status] ?? p.status}</span>,
            meta: (p) => [SUBJECT(p.subject), `drafted ${ago(p.created_at)}`] }} />
      )}
    </Section>
  );
}

type Policy = { id: string; type: string; subject: string; version: number; status: string; body: unknown; body_sha256: string; created_at?: string; approvals: Array<{ role: string; decision: string; created_at: string; comment?: string | null }> };

const STATUS: Record<string, string> = { draft: 'Waiting', pending: 'Waiting', approved: 'Approved', rejected: 'Rejected', superseded: 'Replaced', revoked: 'Withdrawn' };
/** F18 approval rules, readable: the version in words, every version of it (as post revisions), any two side by side, Approve only with fresh MFA, 409 reloads. */
export function PolicyApprove() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { mfaAgeMs } = useAuth();
  const p = useApi<Policy>(`/v1/policies/${id}`);
  const all = useApi<{ data: Policy[] }>(p.data ? `/v1/policies?type=${p.data.type}&subject=${encodeURIComponent(p.data.subject)}` : null);
  const [comment, setComment] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'read' | 'compare'>('read');
  const [from, setFrom] = useState<number | null>(null);
  if (p.isLoading) return <Page title="Approval"><p role="status">Loading…</p></Page>;
  if (p.error || !p.data) return <Page title="Approval"><ErrorBox error={p.error} /></Page>;
  const pol = p.data;
  const versions = [...(all.data?.data ?? [])].sort((a, b) => a.version - b.version);
  const older = versions.filter((x) => x.version < pol.version);
  // compare with the chosen version, else the last approved one before this, else the one just before
  const base = versions.find((x) => x.version === from) ?? [...older].reverse().find((x) => x.status === 'approved' || x.status === 'superseded') ?? older.at(-1);
  const view = policyView(pol.type, pol.body);
  const baseView = base ? policyView(base.type, base.body) : [];
  const marks = base ? itemChanges(baseView, view) : undefined;
  const changed = changeCount(marks);
  const fresh = mfaFresh(mfaAgeMs);
  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(true); setErr(null);
    try {
      await api(`/portal/policies/${pol.id}/${decision}`, { method: 'POST', body: { body_sha256: pol.body_sha256, comment: comment || undefined } });
      await qc.invalidateQueries();
      nav('/approvals');
    } catch (e) {
      // the body changed while the screen was open: reload what is now there
      if (e instanceof ApiError && e.status === 409) { await p.refetch(); setErr(new ApiError(e.code, 'This policy changed while you were reading it. The screen now shows the current version.', 409)); }
      else setErr(e);
    } finally { setBusy(false); }
  };
  const waiting = pol.status === 'pending' || pol.status === 'draft';
  return (
    <Page title={`${KIND[pol.type] ?? label(pol.type)} · version ${pol.version}`}>
      <div className="pv-head">
        <span className={`chip ${pol.status === 'approved' ? 'done' : ''}`}>{STATUS[pol.status] ?? pol.status}</span>
        {waiting ? <span className="muted small">Needs a signature from {APPROVERS[pol.type] ?? 'an approver'}</span> : null}
      </div>
      {versions.length > 1 ? (
        <VersionStrip versions={versions} currentId={pol.id} baseVersion={base?.version} mode={mode} onMode={setMode} onPick={setFrom}
          href={(v) => `/approvals/${v.id}`} statusLabel={(v) => STATUS[v.status] ?? v.status} canCompare={older.length > 0} />
      ) : <p className="muted small">The first version: nothing to compare yet.</p>}
      {mode === 'compare' && base ? (
        <Section title={`Version ${base.version} → version ${pol.version}: ${changed ? `${changed} change${changed === 1 ? '' : 's'}` : 'no changes'}`}>
          {older.length > 1 ? (
            <label className="small">Compare with <select id={`${uid}-f1`} value={base.version} onChange={(e) => setFrom(Number(e.target.value))}>{older.map((v) => <option key={v.id} value={v.version}>version {v.version} ({STATUS[v.status] ?? v.status})</option>)}</select></label>
          ) : null}
          <CompareView older={baseView} newer={view} marks={marks!} olderTitle={`Version ${base.version} · ${STATUS[base.status] ?? base.status}`} olderLabel={`Version ${base.version}`}
            newerTitle={`Version ${pol.version} · ${STATUS[pol.status] ?? pol.status} (this one)`} newerLabel="Policy body" />
        </Section>
      ) : (
        <Section title="What you are approving">
          {all.isLoading ? <p className="muted small" role="status">Loading the earlier versions…</p> : marks && changed ? <p className="small">{changed} change{changed === 1 ? '' : 's'} since version {base!.version}, marked below. <button type="button" className="link" onClick={() => setMode('compare')}>Compare side by side</button></p> : null}
          <ReadView view={view} marks={marks} side="new" label="Policy body" />
        </Section>
      )}
      <Section title="Signatures">
        <Signatures approvals={pol.approvals} />
      </Section>
      {waiting ? (
        <Section title="Decide">
          {!fresh ? (
            <div className="need-steps" role="status">
              <b>First, confirm it's you</b>
              <ol><li>Approving or rejecting needs your second factor (an authenticator code) from the last 12 hours. It takes about 30 seconds.</li>
                <li>You come straight back here, and the buttons unlock.</li></ol>
              <Link className="btn btn-primary" to={`/mfa?next=${encodeURIComponent(`/approvals/${pol.id}`)}`}>Verify my second factor</Link>
            </div>
          ) : null}
          <div className="field"><label htmlFor="comment">Comment (optional)</label><textarea id="comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} /></div>
          {err ? <ErrorBox error={err} doing="record your decision" /> : null}
        </Section>
      ) : null}
      {/* outside the section, so on a phone the bar stays at the bottom of the screen while the policy is read */}
      {waiting ? (
        <div className="actions pv-decide">
          <button type="button" className="btn btn-primary" disabled={!fresh || busy} aria-disabled={!fresh || busy} onClick={() => decide('approve')}>Approve</button>
          <button type="button" className="btn btn-danger" disabled={!fresh || busy} title={fresh ? undefined : 'Verify your second factor first'} onClick={() => decide('reject')}>Reject</button>
        </div>
      ) : null}
      <p className="small muted">Fingerprint {pol.body_sha256.slice(0, 16)}…: the exact text you sign.</p>
    </Page>
  );
}

const APPROVERS: Record<string, string> = {
  icp_version: 'a sales or marketing approver', outbound_policy: 'a sales approver', first_campaign_messages: 'a sales or marketing approver',
  audience_policy: 'a marketing approver', sales_ready_criteria: 'a sales or marketing approver', integration_mapping: 'an owner or admin', spend_cap: 'an owner',
};
